import { createRouter, RouteRecordRaw, createWebHistory } from "vue-router";
import { useUserStore } from "@/stores/user";
import { loginRoutes } from "./routes/login";
import { telematicsRoutes } from "./routes/telematics";
import waybillingRoutes from "./routes/waybilling";
import { reportBuilderRoutes } from "./routes/report-builder";
import visibilityRoutes from "./routes/visibility";
import analyticsRoutes from "./routes/analytics";
import reportsRoutes from "./routes/reports";
import pricingRoutes from "./routes/pricing";
import shipperRoutes from "./routes/shipper";
import adminRoutes from "./routes/admin";
import customerSupportRoutes from "./routes/customer-support";
import { invoicingRoutes } from "./routes/invoicing";
import { settingsRoutes } from "./routes/settings";
import { useTheme } from "./composables/useTheme";
import { oAuthLoginRoutes } from "./routes/callback";
import App404Error from "./components/errors/App404Error.vue";
import { useAuthSession } from "./composables/useAuthSession";
import useNotificationStore from "./stores/notifications";

const AuthenticatedApp = () => import("@/pages/AuthenticatedApp.vue");
const DashboardPage = () => import("@/pages/dashboard/DashboardPage.vue");
const TerminalWrapper = () => import("@/components/terminal/TerminalPage.vue");
const TerminalLoadList = () => import("@/components/terminal/TerminalLoadList.vue");
const TerminalLogin = () => import("@/components/terminal/TerminalLogin.vue");
const UserProfilePage = () => import("@/pages/userProfile/UserProfile.vue");
const PublicShipmentsPage = () => import("@/pages/shipments/PublicShipmentsPage.vue");

const { setTitle } = useTheme();
const { loginRoute, authProvider, sessionExpired } = useAuthSession();

const routes: RouteRecordRaw[] = [
  {
    name: "app",
    path: "/",
    component: AuthenticatedApp,
    beforeEnter: async (to) => {
      const userStore = useUserStore();
      // Just loading the page initially
      if (!userStore.userVerifiedAt) {
        const toPath = to.fullPath;
        localStorage.setItem("toPath", toPath);
        // Current session is no good, or we are using SSO to verify this user's session.
        if (sessionExpired.value || authProvider.value !== "telegraph") {
          // OAuth page(s) handle verification and login as needed
          return loginRoute.value;
        }
        const verified = await userStore.verifyCurrentUser();
        if (!verified) {
          return loginRoute.value;
        }
      }

      if (userStore.userVerifiedAt && userStore.user) {
        if (!userStore.user.companies.length && !userStore.user.is_admin) {
          const notifier = useNotificationStore();
          notifier.setToast("danger", "User is not associated with any customers");
          return { name: "oauthLoginFailed" };
        }

        if (!userStore.user.companies.length && userStore.user.is_admin) {
          if (to.path.startsWith("/admin")) {
            return true;
          } else {
            return { name: "AdminPage" };
          }
        }

        const localId = localStorage.getItem("defaultCompany");
        const parsedLocalId = localId ? JSON.parse(localId) : "";
        const fallbackId = userStore.user.companies[0].id;
        const preferredCustomerId = to.params.customerId?.toString() || parsedLocalId || fallbackId;
        if (!userStore.customerId || preferredCustomerId !== userStore.customerId) {
          if (to.path.startsWith("/admin")) {
            await userStore.setUserCustomer(parsedLocalId || fallbackId);
          } else {
            await userStore.setUserCustomer(preferredCustomerId);
          }
        }
      }

      const setUpAppResponse = await userStore.setUpAppEnvironment();
      if (!setUpAppResponse.success) {
        const notificationStore = useNotificationStore();
        notificationStore.setToast("danger", setUpAppResponse.error?.message);
        return userStore.landingPage;
      }
    },
    children: [
      {
        name: "dashboard",
        path: "/",
        component: DashboardPage,
        meta: {
          authenticated: true,
          permissions: [
            "dashboard_widgets_dwelling_over_2_days",
            "dashboard_widgets_ap_status",
            "dashboard_widgets_cp_status_or_notified",
            "dashboard_widgets_ready_for_release",
          ],
          title: `Dashboard`,
        },
        beforeEnter: (to) => {
          const userStore = useUserStore();
          const target = userStore.routeBasedOffOfEntitlements(to);
          if (target.name === to.name) {
            return true;
          }
          return target;
        },
      },
      visibilityRoutes,
      telematicsRoutes,
      analyticsRoutes,
      reportsRoutes,
      reportBuilderRoutes,
      pricingRoutes,
      invoicingRoutes,
      settingsRoutes,
      waybillingRoutes,
      adminRoutes,
      customerSupportRoutes,
      {
        name: "userProfile",
        path: "/profile",
        component: UserProfilePage,
        meta: { authenticated: true, title: `My Profile` },
      },
    ],
  },
  shipperRoutes,
  loginRoutes,
  oAuthLoginRoutes,
  {
    name: "terminal",
    path: "/terminal",
    component: TerminalWrapper,
    meta: {
      permissions: ["terminal_terminal"],
    },
    children: [
      {
        name: "terminalLogin",
        path: "login",
        component: TerminalLogin,
        meta: {
          authenticated: false,
          title: `Terminal Login`,
        },
      },
      {
        name: "terminalLoadList",
        path: "loads",
        alias: "/terminal",
        component: TerminalLoadList,
        meta: { title: `Terminal View`, authenticated: true },
      },
    ],
  },
  {
    name: "share",
    path: "/share/:shareId",
    component: PublicShipmentsPage,
    meta: {
      authenticated: false,
    },
  },
  { path: "/:pathMatch(.*)*", name: "NotFound", component: App404Error },
];

const router = createRouter({
  routes,
  history: createWebHistory(),
  scrollBehavior(to, from) {
    if (to.name !== from.name) {
      return { top: 0 };
    }
  },
});

router.beforeEach(async (to) => {
  if (to.meta?.title) {
    setTitle(`${to.meta.title}`);
  }

  if (to.meta?.authenticated) {
    const userStore = useUserStore();
    if (userStore.user && userStore.customerId) {
      if (to.path.startsWith("/admin") && userStore.user.is_admin) {
        return true;
      }

      const target = userStore.routeBasedOffOfEntitlements(to);
      if (target.name === to.name) {
        return true;
      }
      return target;
    }
  }
});

router.onError((e) => {
  console.error("ERROR IN ROUTER: ", e);
  console.error(router.currentRoute);
});
export default router;
